/**
 * @file ReportScheduleDialog.jsx
 * @description Add / edit dialog for a report delivery schedule.
 *              Run times are entered in the client time zone (Configuration →
 *              Time Settings), which is surfaced next to the time field.
 * @component ReportScheduleDialog
 * @exports ReportScheduleDialog
 */

import React, { useEffect, useMemo, useState } from "react";
import {
    Alert, Box, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem,
    Select, Switch, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/Schedule";

import {
    GRANULARITY_OPTIONS, TIME_RANGE_OPTIONS, RECURRENCE, RECURRENCE_OPTIONS,
    DAY_OF_WEEK_OPTIONS, DAY_OF_MONTH_OPTIONS, NEW_SCHEDULE,
    REPORT_STRINGS, EMAIL_RE, resolveTimeZone, describeSchedule,
    MAX_SCHEDULE_NAME, MAX_DESCRIPTION,
} from "../../common/constants/reportModule/reportConstants.js";

const BLUE  = "#378ADD";
const GREEN = "#1D9E75";
const PINK  = "#D4537E";

const todayStr = () => {
    const d  = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
};

// ── Small building blocks ──────────────────────────────────────────
function Field({ label, hint, required, children }) {
    return (
        <Box sx={{ mb: 2.5 }}>
            <Typography sx={{
                fontSize: 11, fontWeight: 500, color: "text.secondary",
                textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.75,
            }}>
                {label}{required && <Box component="span" sx={{ color: PINK, ml: 0.3 }}>*</Box>}
            </Typography>
            {children}
            {hint && (
                <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                    {hint}
                </Typography>
            )}
        </Box>
    );
}

/** Pill segmented control — same visual language as the dashboard channel toggle. */
function Segmented({ options, value, onChange }) {
    return (
        <Box sx={{
            display: "inline-flex", flexWrap: "wrap",
            border: "0.5px solid", borderColor: "divider",
            borderRadius: "20px", overflow: "hidden",
        }}>
            {options.map(o => {
                const on = o.value === value;
                return (
                    <Box key={o.value} onClick={() => onChange(o.value)} title={o.hint} sx={{
                        px: "16px", py: "6px", fontSize: 13,
                        fontWeight: on ? 500 : 400,
                        cursor: "pointer", userSelect: "none",
                        background: on ? BLUE : "transparent",
                        color:      on ? "#fff" : "text.secondary",
                        transition: "all .15s",
                        "&:hover": on ? {} : { background: "rgba(55,138,221,0.06)" },
                    }}>{o.label}</Box>
                );
            })}
        </Box>
    );
}

/** Compact weekday picker for the weekly recurrence. */
function DayOfWeekPicker({ value, onChange }) {
    return (
        <Box sx={{ display: "flex", gap: 0.5 }}>
            {DAY_OF_WEEK_OPTIONS.map(d => {
                const on = d.value === Number(value);
                return (
                    <Box key={d.value} onClick={() => onChange(d.value)} title={d.full} sx={{
                        width: 42, py: "6px", textAlign: "center",
                        fontSize: 12, fontWeight: on ? 500 : 400,
                        borderRadius: "16px", cursor: "pointer", userSelect: "none",
                        border: "0.5px solid",
                        borderColor: on ? BLUE : "divider",
                        background: on ? BLUE : "transparent",
                        color: on ? "#fff" : "text.secondary",
                        transition: "all .15s",
                        "&:hover": on ? {} : { background: "rgba(55,138,221,0.06)" },
                    }}>{d.label}</Box>
                );
            })}
        </Box>
    );
}

// ── Component ──────────────────────────────────────────────────────
const ReportScheduleDialog = ({ open, schedule, catalog = [], tzKey, saving, onClose, onSave }) => {
    const [draft,      setDraft]      = useState({ ...NEW_SCHEDULE });
    const [emailInput, setEmailInput] = useState("");
    const [emailError, setEmailError] = useState("");
    const [formError,  setFormError]  = useState("");

    useEffect(() => {
        setDraft({ ...NEW_SCHEDULE, ...(schedule || {}) });
        setEmailInput(""); setEmailError(""); setFormError("");
    }, [schedule]);

    const isEdit = !!draft.scheduleId;
    const set    = (patch) => setDraft(d => ({ ...d, ...patch }));
    const tz     = resolveTimeZone(tzKey);

    const selectedReport = useMemo(
        () => catalog.find(r => String(r.reportId) === String(draft.reportId)) || null,
        [catalog, draft.reportId]
    );

    /** Switching recurrence backfills the companion field it needs. */
    const setRecurrence = (value) => {
        const patch = { recurrence: value };
        if (value === RECURRENCE.WEEKLY  && !draft.dayOfWeek)   patch.dayOfWeek  = 1;
        if (value === RECURRENCE.MONTHLY && draft.dayOfMonth == null) patch.dayOfMonth = 1;
        if (value === RECURRENCE.NONE    && !draft.runDate)     patch.runDate    = todayStr();
        set(patch);
        setFormError("");
    };

    // ── Recipients ────────────────────────────────────────────────
    const addEmail = (raw) => {
        // Accept a paste of several addresses at once.
        const parts = String(raw).split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
        if (!parts.length) return;

        const next = [...draft.recipients];
        let err = "";
        for (const p of parts) {
            if (!EMAIL_RE.test(p))                                   { err = REPORT_STRINGS.INVALID_EMAIL;   continue; }
            if (next.some(e => e.toLowerCase() === p.toLowerCase())) { err = REPORT_STRINGS.DUPLICATE_EMAIL; continue; }
            next.push(p);
        }
        set({ recipients: next });
        setEmailError(err);
        if (!err) setEmailInput("");
    };

    const removeEmail = (email) =>
        set({ recipients: draft.recipients.filter(e => e !== email) });

    const handleEmailKeyDown = (e) => {
        if (["Enter", ",", ";", " ", "Tab"].includes(e.key) && emailInput.trim()) {
            if (e.key !== "Tab") e.preventDefault();
            addEmail(emailInput);
        } else if (e.key === "Backspace" && !emailInput && draft.recipients.length) {
            removeEmail(draft.recipients[draft.recipients.length - 1]);
        }
    };

    // ── Save ──────────────────────────────────────────────────────
    const handleSave = () => {
        // Fold a half-typed address in rather than silently dropping it.
        let recipients = draft.recipients;
        if (emailInput.trim()) {
            const p = emailInput.trim();
            if (!EMAIL_RE.test(p)) { setEmailError(REPORT_STRINGS.INVALID_EMAIL); return; }
            if (!recipients.some(e => e.toLowerCase() === p.toLowerCase())) {
                recipients = [...recipients, p];
            }
        }

        if (!draft.reportId)                        return setFormError(REPORT_STRINGS.REQUIRE_REPORT);
        if (!draft.scheduleName.trim())             return setFormError(REPORT_STRINGS.REQUIRE_NAME);
        if (!recipients.length)                     return setFormError(REPORT_STRINGS.REQUIRE_RECIPIENT);
        if (!draft.includePdf && !draft.includeCsv) return setFormError(REPORT_STRINGS.REQUIRE_OUTPUT);
        if (!draft.generationTime)                  return setFormError(REPORT_STRINGS.REQUIRE_TIME);
        if (draft.recurrence === RECURRENCE.NONE) {
            if (!draft.runDate)                     return setFormError(REPORT_STRINGS.REQUIRE_RUN_DATE);
            if (draft.runDate < todayStr())         return setFormError(REPORT_STRINGS.PAST_RUN_DATE);
        }

        setFormError("");
        setEmailInput("");
        onSave({
            ...draft,
            recipients,
            scheduleName: draft.scheduleName.trim(),
            description:  (draft.description || "").trim(),
        });
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}>

            <DialogTitle sx={{
                fontSize: 15, fontWeight: 500, py: 1.5,
                borderBottom: "0.5px solid", borderColor: "divider",
                display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
                {isEdit ? REPORT_STRINGS.EDIT_SCHEDULE : REPORT_STRINGS.ADD_SCHEDULE}
                <IconButton size="small" onClick={onClose} disabled={saving}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2.5 }}>
                {formError && <Alert severity="warning" sx={{ mb: 2 }}>{formError}</Alert>}

                {/* Report */}
                <Field label="Report" required hint={selectedReport?.description}>
                    <Select
                        fullWidth size="small" displayEmpty
                        value={draft.reportId ?? ""}
                        onChange={(e) => set({ reportId: e.target.value })}
                        sx={{ fontSize: 13 }}
                    >
                        <MenuItem value="" disabled sx={{ fontSize: 13 }}>Select a report</MenuItem>
                        {catalog.map(r => (
                            <MenuItem key={r.reportId} value={r.reportId} sx={{ fontSize: 13 }}>
                                {r.reportName}
                            </MenuItem>
                        ))}
                    </Select>
                </Field>

                {/* Schedule name */}
                <Field label="Schedule name" required hint="Shown in the schedule list.">
                    <TextField
                        fullWidth size="small"
                        placeholder="e.g. Monday PTP recap — Collections leads"
                        value={draft.scheduleName}
                        onChange={(e) => set({ scheduleName: e.target.value })}
                        inputProps={{ maxLength: MAX_SCHEDULE_NAME, style: { fontSize: 13 } }}
                    />
                </Field>

                {/* Description */}
                <Field
                    label="Description"
                    hint="What this schedule is for and who reads it. Optional."
                >
                    <TextField
                        fullWidth size="small" multiline minRows={2} maxRows={4}
                        placeholder="e.g. Weekly recap for the collections floor leads — tracks PTP kept vs. broken."
                        value={draft.description}
                        onChange={(e) => set({ description: e.target.value })}
                        inputProps={{ maxLength: MAX_DESCRIPTION, style: { fontSize: 13 } }}
                    />
                    <Typography sx={{
                        fontSize: 11, color: "text.secondary", mt: 0.5, textAlign: "right",
                    }}>
                        {(draft.description || "").length}/{MAX_DESCRIPTION}
                    </Typography>
                </Field>

                {/* Recurrence */}
                <Field
                    label="Recurrence"
                    required
                    hint={draft.recurrence === RECURRENCE.NONE ? REPORT_STRINGS.ONCE_NOTE : undefined}
                >
                    <Segmented
                        options={RECURRENCE_OPTIONS}
                        value={draft.recurrence}
                        onChange={setRecurrence}
                    />

                    {/* Companion field for the chosen recurrence */}
                    {draft.recurrence === RECURRENCE.WEEKLY && (
                        <Box sx={{ mt: 1.5 }}>
                            <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.75 }}>
                                On which day
                            </Typography>
                            <DayOfWeekPicker
                                value={draft.dayOfWeek}
                                onChange={(v) => set({ dayOfWeek: v })}
                            />
                        </Box>
                    )}

                    {draft.recurrence === RECURRENCE.MONTHLY && (
                        <Box sx={{ mt: 1.5 }}>
                            <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.75 }}>
                                On which day
                            </Typography>
                            <Select
                                size="small"
                                value={draft.dayOfMonth ?? 1}
                                onChange={(e) => set({ dayOfMonth: Number(e.target.value) })}
                                sx={{ width: 150, fontSize: 13 }}
                            >
                                {DAY_OF_MONTH_OPTIONS.map(d => (
                                    <MenuItem key={d.value} value={d.value} sx={{ fontSize: 13 }}>
                                        {d.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                                Days run to the 28th so every month is covered — use “Last day” for month-end.
                            </Typography>
                        </Box>
                    )}

                    {draft.recurrence === RECURRENCE.NONE && (
                        <Box sx={{ mt: 1.5 }}>
                            <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.75 }}>
                                On which date
                            </Typography>
                            <TextField
                                size="small" type="date"
                                value={draft.runDate || ""}
                                onChange={(e) => set({ runDate: e.target.value })}
                                inputProps={{ min: todayStr(), style: { fontSize: 13 } }}
                                sx={{ width: 180 }}
                            />
                        </Box>
                    )}
                </Field>

                {/* Run time — in the client zone */}
                <Field label="Runs at" required>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <TextField
                            size="small" type="time"
                            value={draft.generationTime}
                            onChange={(e) => set({ generationTime: e.target.value })}
                            inputProps={{ step: 300, style: { fontSize: 13 } }}
                            sx={{ width: 150 }}
                        />
                        <Box sx={{
                            display: "inline-flex", alignItems: "center", gap: "6px",
                            px: "10px", py: "5px", borderRadius: "20px",
                            border: "0.5px solid", borderColor: "divider",
                            background: "#f7f7f7", fontSize: 12, color: "text.secondary",
                        }}>
                            <ScheduleIcon sx={{ fontSize: 14, color: BLUE }} />
                            {tz.name} ({tz.key})
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                        {REPORT_STRINGS.TZ_SOURCE}
                    </Typography>
                </Field>

                {/* Recipients */}
                <Field
                    label="Recipients"
                    required
                    hint="Type an address and press Enter. Paste a list to add several at once."
                >
                    <Box sx={{
                        border: "0.5px solid", borderColor: emailError ? PINK : "divider",
                        borderRadius: 1, p: 1,
                        display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center",
                        minHeight: 42,
                        "&:focus-within": { borderColor: BLUE },
                    }}>
                        {draft.recipients.map(email => (
                            <Chip
                                key={email}
                                label={email}
                                size="small"
                                onDelete={() => removeEmail(email)}
                                sx={{ fontSize: 12, height: 24, borderRadius: "12px" }}
                            />
                        ))}
                        <Box
                            component="input"
                            value={emailInput}
                            placeholder={draft.recipients.length ? "" : "name@company.com"}
                            onChange={(e) => { setEmailInput(e.target.value); setEmailError(""); }}
                            onKeyDown={handleEmailKeyDown}
                            onBlur={() => emailInput.trim() && addEmail(emailInput)}
                            sx={{
                                flex: 1, minWidth: 160, border: "none", outline: "none",
                                fontSize: 13, background: "transparent",
                                fontFamily: "inherit", color: "inherit", p: "2px",
                            }}
                        />
                    </Box>
                    {emailError && (
                        <Typography sx={{ fontSize: 11, color: PINK, mt: 0.5 }}>{emailError}</Typography>
                    )}
                </Field>

                {/* Granularity */}
                <Field label="Granularity" hint="How rows are grouped inside the report.">
                    <Segmented
                        options={GRANULARITY_OPTIONS}
                        value={draft.granularity}
                        onChange={(v) => set({ granularity: v })}
                    />
                </Field>

                {/* Time range */}
                <Field label="Time range" hint="The window of data pulled on each run.">
                    <Segmented
                        options={TIME_RANGE_OPTIONS}
                        value={draft.timeRange}
                        onChange={(v) => set({ timeRange: v })}
                    />
                </Field>

                {/* Outputs */}
                <Field label="Outputs" required>
                    <Box sx={{ display: "flex", gap: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={!!draft.includePdf}
                                    onChange={(e) => set({ includePdf: e.target.checked })}
                                    sx={{ color: BLUE, "&.Mui-checked": { color: BLUE } }}
                                />
                            }
                            label={<Typography sx={{ fontSize: 13 }}>Graphs (PDF)</Typography>}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={!!draft.includeCsv}
                                    onChange={(e) => set({ includeCsv: e.target.checked })}
                                    sx={{ color: GREEN, "&.Mui-checked": { color: GREEN } }}
                                />
                            }
                            label={<Typography sx={{ fontSize: 13 }}>Data file (CSV)</Typography>}
                        />
                    </Box>
                </Field>

                {/* Active */}
                <Field label="Status">
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={!!draft.isEnabled}
                                onChange={(e) => set({ isEnabled: e.target.checked })}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: 13 }}>
                                {draft.isEnabled ? "Active — sends on schedule" : "Paused — nothing sends"}
                            </Typography>
                        }
                    />
                </Field>

                {/* Plain-language recap of everything above */}
                <Box sx={{
                    background: "rgba(55,138,221,0.06)", borderRadius: 2,
                    p: "10px 14px", mb: 1,
                }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        {describeSchedule(draft, tzKey)} ·{" "}
                        {draft.recipients.length || "no"} recipient{draft.recipients.length === 1 ? "" : "s"} ·{" "}
                        {[draft.includePdf && "PDF", draft.includeCsv && "CSV"].filter(Boolean).join(" + ") || "no output"}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{
                px: 3, py: 2, gap: 1,
                borderTop: "0.5px solid", borderColor: "divider",
            }}>
                <Box onClick={saving ? undefined : onClose} sx={{
                    px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                    border: "0.5px solid", borderColor: "divider",
                    color: "text.secondary", cursor: saving ? "default" : "pointer",
                    userSelect: "none",
                }}>Cancel</Box>

                <Box onClick={saving ? undefined : handleSave} sx={{
                    px: "18px", py: "6px", fontSize: 13, borderRadius: "20px",
                    background: BLUE, color: "#fff", fontWeight: 500,
                    cursor: saving ? "default" : "pointer", userSelect: "none",
                    opacity: saving ? 0.6 : 1,
                    display: "inline-flex", alignItems: "center", gap: 1,
                    "&:hover": saving ? {} : { background: "#2a6db5" },
                }}>
                    {saving && <CircularProgress size={13} sx={{ color: "#fff" }} />}
                    {isEdit ? "Save changes" : "Create schedule"}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default ReportScheduleDialog;